///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Render scene: the JSON an automated event video is rendered from. The
/// generalization of the storm-only job weather-services builds today
/// (src/video/storm-videos.ts RenderJob): a subject of any dataset kind,
/// the authored framing, the visible layers, an optional cutout block, the
/// scene-time window and an optional shot script. Authored in the app with
/// __ebDebug.exportRenderScene() (earthbrowser/src/utils/renderScene.ts),
/// consumed by video-renderer/renderer/job.py and the weather-services
/// trigger, which layer their per-kind templates (camera path, overlay,
/// data attribution) on top of whatever the scene leaves unset.
///

import type { SavedViewCamera } from './saved-views'

export const cRenderSceneVersion = 1

/** Dataset kinds a video can be about; "coords" is a free camera view */
export type RenderSubjectKind =
	| 'hurricane'
	| 'earthquake'
	| 'volcano'
	| 'wildfire'
	| 'alert'
	| 'buoy'
	| 'webcam'
	| 'satellite'
	| 'location'
	| 'placemark'
	| 'nexrad'
	| 'coords'

export interface RenderSubject {
	kind: RenderSubjectKind
	/** Dataset entity id (storm id, quake id, ...); absent for coords */
	id?: string
	/** The app's ?target= value the subject came from (storm-ep172026, quake-...) */
	target?: string
	/**
	 * Subject position. For a fixed subject this is where the camera looks;
	 * for a moving one (storm) it is the position at authoring time and the
	 * renderer follows the track instead.
	 */
	lat: number
	lon: number
	/** Display name for the overlay ("Hurricane Melissa", "Kīlauea") */
	name?: string
}

/**
 * The authored framing: the surface point at screen center plus the
 * camera's altitude, tilt and bearing (what __ebRender.setCameraLookAt
 * takes). `pose` is the same camera in raw engine units so the scene can
 * be flown to in the app again.
 */
export interface RenderCamera {
	lookAt: { lat: number; lon: number }
	altitudeKm: number
	tiltDeg: number
	bearingDeg: number
	pose: SavedViewCamera
}

/** Cross-section / column block to open on the subject (needs the matching layer) */
export interface RenderCutout {
	kind: 'quake' | 'storm'
	id: string
}

/** Scene-time window of the time-lapse (epoch ms) and the step between frames */
export interface RenderClock {
	start: number
	end: number
	stepMinutes: number
}

/**
 * One continuous camera move (renderer/job.py Shot). `clock` pins scene
 * time to the window's first/last frame or sweeps it ("lapse", which also
 * sets the frame count from the step). Fields left unset fall back to the
 * renderer's per-kind defaults.
 */
export interface RenderShot {
	kind: 'establish' | 'lapse' | 'pullback'
	clock?: 'start' | 'end' | 'lapse'
	seconds?: number
	altitudeKm?: number
	altitudeFromKm?: number
	altitudeToKm?: number
	tiltDeg?: number
	tiltFromDeg?: number
	tiltToDeg?: number
	bearingSweepDeg?: number
}

/** Per-layer options that affect the picture (mirrors app state the layers read) */
export interface RenderLayerSettings {
	/** NHC wind speed probability threshold the swath/contours show (kt) */
	stormWindProbabilityKt?: 34 | 50 | 64
}

export interface RenderOverlayText {
	title?: string
	subtitle?: string
}

export interface RenderScene {
	version: typeof cRenderSceneVersion
	subject: RenderSubject
	camera: RenderCamera
	/** Dataset layer ids (the app's visibleDatasets vocabulary) to show; enable-only */
	layers: string[]
	layerSettings?: RenderLayerSettings
	cutout?: RenderCutout
	clock: RenderClock
	/** Cloud look: white relief clouds or the enhanced-IR palette */
	style: 'clean' | 'ir'
	/** Portrait reel (1080x1920) or landscape (1920x1080) */
	format: 'reel' | 'landscape'
	fps?: number
	/** Shot script; unset = the renderer's template for the subject kind */
	shots?: RenderShot[]
	overlay?: RenderOverlayText
	/** Embed to capture; unset = the production embed */
	appUrl?: string
	/** Where and when the scene was authored */
	authored: {
		at: string
		appVersion: string
		/** App URL reproducing the authored view (camera, layers, target) */
		url: string
	}
}
